import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, ChevronLeft, ChevronRight, RotateCcw, AlertTriangle, CheckCircle2,
  Search, X, Wallet, ArrowUp, ArrowDown, ArrowUpDown, Filter, Calendar, Coins, TrendingUp, TrendingDown, Activity, Copy,
} from 'lucide-react';

const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

const STATUS_META = {
  ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  overpaid: { label: 'Sovrapagato', cls: 'bg-red-100 text-red-700 border-red-200', icon: TrendingUp },
  underpaid: { label: 'Sottopagato', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: TrendingDown },
  no_budget: { label: 'No budget', cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: AlertTriangle },
};

export default function OwnerCostsOverviewPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Diagnostica connessione Holiday World (per capire blocchi di rete in produzione)
  const [diagLoading, setDiagLoading] = useState(false);
  const [diag, setDiag] = useState(null);

  // Sync PRENOTAZIONI HW (il bottone "Sync HW ora" sincronizza solo le proprietà)
  const [bkSyncing, setBkSyncing] = useState(false);
  const [bkResult, setBkResult] = useState(null);

  // Filtri
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('all');
  const [fMethod, setFMethod] = useState('all');

  // Sort
  const [sortBy, setSortBy] = useState('property_name');
  const [sortDir, setSortDir] = useState('asc');

  const fetchData = async (y = year) => {
    setLoading(true);
    try {
      const r = await api.get(`/owner-costs/overview?year=${y}`);
      setData(r.data);
    } catch (e) {
      toast.error('Errore caricamento overview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(year); /* eslint-disable-next-line */ }, [year]);

  const triggerHwSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/admin/hw-sync/properties/run-now');
      toast.success(`Sync OK: ${r.data.synced} sincronizzati, ${r.data.updated} aggiornati`);
      fetchData(year);
    } catch (e) {
      toast.error('Errore sync: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const runHwDiagnostic = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await api.get('/admin/hw-sync/diagnostic');
      setDiag(r.data);
      if (r.data?.health_status === 200) toast.success('Connessione HW OK');
      else toast.error('Connessione HW NON riuscita — vedi diagnostica');
    } catch (e) {
      toast.error('Errore diagnostica: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDiagLoading(false);
    }
  };

  const copyDiag = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
      toast.success('Diagnostica copiata negli appunti');
    } catch {
      toast.error('Copia non riuscita');
    }
  };

  // Lancia il sync delle PRENOTAZIONI (background) e poi mostra l'esito leggendo lo stato.
  const triggerBookingsSync = async () => {
    setBkSyncing(true);
    setBkResult(null);
    let prevLastSync = null;
    try {
      try {
        const st0 = await api.get('/hw-sync/status');
        prevLastSync = st0.data?.bookings?.last_sync || null;
      } catch { /* ignora */ }

      await api.post('/hw-sync/bookings?days_back=30');
      toast.success('Sync prenotazioni avviato in background. Attendo l\'esito...');

      // Polling stato: il sync gira sul server, qui leggiamo il log appena aggiornato.
      let done = false;
      for (let i = 0; i < 24 && !done; i++) {
        await new Promise(r => setTimeout(r, 6000));
        try {
          const st = await api.get('/hw-sync/status');
          const b = st.data?.bookings || {};
          setBkResult(b);
          if (b.last_sync && b.last_sync !== prevLastSync) {
            done = true;
            toast.success(`Sync prenotazioni completato: ${b.synced || 0} nuove, ${b.updated || 0} aggiornate, ${b.errors || 0} errori`);
            fetchData(year);
          }
        } catch { /* continua il polling */ }
      }
      if (!done) {
        toast.info('Sync ancora in corso sul server. Riapri tra poco per vedere l\'esito aggiornato.');
      }
    } catch (e) {
      toast.error('Errore avvio sync prenotazioni: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setBkSyncing(false);
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 inline ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 inline ml-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 inline ml-1 text-blue-600" />;
  };

  const filteredSorted = useMemo(() => {
    if (!data?.items) return [];
    const q = search.toLowerCase().trim();
    let arr = data.items.filter(it => {
      if (fStatus !== 'all' && it.status !== fStatus) return false;
      if (fMethod !== 'all' && (it.owner_payment_method || 'bank_transfer') !== fMethod) return false;
      if (q) {
        const hay = `${it.property_code || ''} ${it.property_name || ''} ${it.owner_name || ''} ${it.owner_email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const va = a[sortBy] ?? '';
      const vb = b[sortBy] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return arr;
  }, [data, search, fStatus, fMethod, sortBy, sortDir]);

  const isCurrentYearWarning = year === 2026; // Banner solo per il primo anno

  return (
    <Layout>
      <div className="space-y-4 p-6" data-testid="owner-costs-overview-page">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Coins className="h-7 w-7 text-emerald-700" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Spese annuali immobili</h1>
              <p className="text-sm text-slate-500">Monitoraggio budget COSTS e pagamenti effettivi per proprietario</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setYear(year - 1)} className="p-2 hover:bg-slate-100 rounded">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-bold text-lg w-16 text-center" data-testid="current-year">{year}</span>
            <button onClick={() => setYear(year + 1)} className="p-2 hover:bg-slate-100 rounded">
              <ChevronRight className="h-4 w-4" />
            </button>
            <Button size="sm" variant="outline" onClick={triggerHwSync} disabled={syncing}
              data-testid="hw-sync-properties-now"
              title="Sincronizza le PROPRIETÀ (anagrafiche + budget costi) da Holiday World">
              {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Sync Proprietà
            </Button>
            <Button size="sm" variant="outline" onClick={triggerBookingsSync} disabled={bkSyncing}
              data-testid="hw-sync-bookings-now"
              title="Sincronizza le PRENOTAZIONI degli ultimi 30 giorni da Holiday World (gira in background)">
              {bkSyncing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
              Sync Prenotazioni
            </Button>
            <Button size="sm" variant="outline" onClick={runHwDiagnostic} disabled={diagLoading}
              data-testid="hw-diagnostic-btn"
              title="Verifica la connessione di rete verso Holiday World e mostra l'IP di uscita di questo ambiente">
              {diagLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Activity className="h-3.5 w-3.5 mr-1" />}
              Diagnostica HW
            </Button>
          </div>
        </div>

        {/* Banner avviso 2026 */}
        {isCurrentYearWarning && (
          <Card className="p-3 bg-amber-50 border-amber-300">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="text-amber-900">
                <strong>Dati 2026 potenzialmente incompleti.</strong> Alcuni pagamenti SEPA effettuati
                prima del 20/05/2026 potrebbero non essere tracciati dal sistema. Da gennaio 2027 i dati
                saranno a regime e i calcoli completamente attendibili.
              </div>
            </div>
          </Card>
        )}

        {/* Diagnostica connessione Holiday World */}
        {diag && (
          <Card className="p-4 border-2" data-testid="hw-diagnostic-result"
            style={{ borderColor: diag.health_status === 200 ? '#10b981' : '#ef4444' }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Activity className={`h-5 w-5 ${diag.health_status === 200 ? 'text-emerald-600' : 'text-red-600'}`} />
                <h3 className="font-bold text-slate-900">Diagnostica connessione Holiday World</h3>
              </div>
              <Button size="sm" variant="ghost" onClick={copyDiag} data-testid="hw-diagnostic-copy">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copia
              </Button>
            </div>

            <div className={`mt-3 p-3 rounded-lg text-sm font-medium ${diag.health_status === 200 ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}
              data-testid="hw-diagnostic-verdict">
              {diag.verdict || '—'}
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <div className="p-3 rounded-lg bg-slate-50 border">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">IP di uscita (questo ambiente)</div>
                <div className="font-mono font-bold text-lg text-slate-900" data-testid="hw-diagnostic-egress-ip">
                  {diag.outbound_ip || diag.outbound_ip_error || '—'}
                </div>
                <div className="text-xs text-slate-400 mt-1">Da autorizzare sul firewall HW</div>
              </div>
              <div className="p-3 rounded-lg bg-slate-50 border">
                <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Esito chiamate</div>
                <div className="space-y-0.5">
                  <div>HTTP /health: <span className="font-mono font-semibold">{diag.health_status ?? (diag.health_error || '—')}</span></div>
                  <div>DNS {diag.target_host}: <span className="font-mono">{diag.dns?.ok ? (diag.dns.ips || []).join(', ') : (diag.dns?.error || '—')}</span></div>
                  <div>TCP :{diag.target_port}: <span className="font-mono">{diag.tcp_443?.ok ? `OK (${diag.tcp_443.ms}ms)` : `${diag.tcp_443?.error || '—'} (${diag.tcp_443?.ms ?? '?'}ms)`}</span></div>
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-400 mt-2">Testato: {diag.tested_at}</div>
          </Card>
        )}

        {/* Esito Sync Prenotazioni HW */}
        {bkResult && (
          <Card className="p-4 border-2 border-blue-200 bg-blue-50/40" data-testid="hw-bookings-sync-result">
            <div className="flex items-center gap-2 mb-2">
              <RotateCcw className={`h-4 w-4 text-blue-600 ${bkSyncing ? 'animate-spin' : ''}`} />
              <h3 className="font-bold text-slate-900 text-sm">Esito Sync Prenotazioni HW</h3>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <div><span className="text-slate-500">Nuove:</span> <span className="font-bold text-emerald-700">{bkResult.synced ?? 0}</span></div>
              <div><span className="text-slate-500">Aggiornate:</span> <span className="font-bold text-blue-700">{bkResult.updated ?? 0}</span></div>
              <div><span className="text-slate-500">Errori:</span> <span className={`font-bold ${(bkResult.errors ?? 0) > 0 ? 'text-red-700' : 'text-slate-600'}`}>{bkResult.errors ?? 0}</span></div>
              <div><span className="text-slate-500">Ultimo sync:</span> <span className="font-mono text-xs">{bkResult.last_sync || '—'}</span></div>
            </div>
            {bkSyncing && <div className="text-xs text-slate-400 mt-2">Sync in corso sul server, aggiornamento automatico...</div>}
          </Card>
        )}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-3">
              <div className="text-xs text-slate-500 uppercase tracking-wider">Immobili</div>
              <div className="text-2xl font-bold text-slate-900">{data.items?.length || 0}</div>
            </Card>
            <Card className="p-3 bg-emerald-50 border-emerald-200">
              <div className="text-xs text-emerald-700 uppercase tracking-wider">OK</div>
              <div className="text-2xl font-bold text-emerald-700">{data.ok_count || 0}</div>
            </Card>
            <Card className="p-3 bg-amber-50 border-amber-200">
              <div className="text-xs text-amber-700 uppercase tracking-wider">Sottopagati</div>
              <div className="text-2xl font-bold text-amber-700">{data.underpaid_count || 0}</div>
            </Card>
            <Card className="p-3 bg-red-50 border-red-200">
              <div className="text-xs text-red-700 uppercase tracking-wider">Sovrapagati</div>
              <div className="text-2xl font-bold text-red-700">{data.overpaid_count || 0}</div>
            </Card>
            <Card className="p-3 bg-slate-50 border-slate-200">
              <div className="text-xs text-slate-600 uppercase tracking-wider">Senza budget</div>
              <div className="text-2xl font-bold text-slate-600">{data.no_budget_count || 0}</div>
            </Card>
          </div>
        )}

        {/* Filtri */}
        <Card className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca immobile, proprietario, codice..."
                className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
                data-testid="search-input" />
            </div>
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="filter-status">
              <option value="all">Tutti gli stati</option>
              <option value="ok">OK</option>
              <option value="overpaid">Sovrapagati</option>
              <option value="underpaid">Sottopagati</option>
              <option value="no_budget">Senza budget</option>
            </select>
            <select value={fMethod} onChange={(e) => setFMethod(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="filter-method">
              <option value="all">Tutti i metodi</option>
              <option value="bank_transfer">Bonifico SEPA</option>
              <option value="paypal">PayPal</option>
            </select>
            {(search || fStatus !== 'all' || fMethod !== 'all') && (
              <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setFStatus('all'); setFMethod('all'); }}>
                <X className="h-3.5 w-3.5 mr-1" /> Reset
              </Button>
            )}
          </div>
        </Card>

        {/* Tabella */}
        {loading ? (
          <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
        ) : filteredSorted.length === 0 ? (
          <Card className="p-8 text-center text-slate-400">Nessun immobile trovato</Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('property_name')}>
                      Immobile <SortIcon col="property_name" />
                    </th>
                    <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_name')}>
                      Proprietario <SortIcon col="owner_name" />
                    </th>
                    <th className="px-3 py-2 text-center cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_payment_method')}>
                      Metodo <SortIcon col="owner_payment_method" />
                    </th>
                    <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_annual_costs_budget')}>
                      Budget anno <SortIcon col="owner_annual_costs_budget" />
                    </th>
                    <th className="px-3 py-2 text-right">Coeff €/gg</th>
                    <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('occupied_days_ytd')}>
                      Giorni YTD <SortIcon col="occupied_days_ytd" />
                    </th>
                    <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('expected_costs_ytd')}>
                      Atteso YTD <SortIcon col="expected_costs_ytd" />
                    </th>
                    <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('paid_costs_total_ytd')}>
                      Pagato YTD <SortIcon col="paid_costs_total_ytd" />
                    </th>
                    <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('delta_vs_expected')}>
                      Δ vs atteso <SortIcon col="delta_vs_expected" />
                    </th>
                    <th className="px-3 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map(it => {
                    const meta = STATUS_META[it.status] || STATUS_META.no_budget;
                    const Icon = meta.icon;
                    const isPaypal = (it.owner_payment_method || 'bank_transfer') === 'paypal';
                    return (
                      <tr key={it.property_id} className="border-t hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <div className="font-medium">{it.property_name || '—'}</div>
                          <div className="text-xs text-slate-500">{it.property_code}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div>{it.owner_name || '—'}</div>
                          <div className="text-xs text-slate-500">{it.owner_email || ''}</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {isPaypal ? (
                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">PayPal</span>
                          ) : (
                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-medium">Bonifico</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {it.owner_annual_costs_budget != null ? fmt(it.owner_annual_costs_budget) : <span className="text-slate-400">—</span>}
                          {it.owner_costs_carry_over > 0 && (
                            <div className="text-[10px] text-emerald-600">+{fmt(it.owner_costs_carry_over)} carry</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          {it.owner_daily_cost_incidence != null ? fmt(it.owner_daily_cost_incidence) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-slate-600">{it.occupied_days_ytd || 0}</td>
                        <td className="px-3 py-2 text-right text-xs">
                          {it.expected_costs_ytd != null ? fmt(it.expected_costs_ytd) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-bold">
                          {fmt(it.paid_costs_total_ytd)}
                          {(it.paid_costs_sepa_ytd > 0 && it.paid_costs_paypal_ytd > 0) && (
                            <div className="text-[10px] text-slate-500">SEPA {fmt(it.paid_costs_sepa_ytd)} + PP {fmt(it.paid_costs_paypal_ytd)}</div>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${it.delta_vs_expected > 0 ? 'text-red-600' : it.delta_vs_expected < 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                          {it.delta_vs_expected != null ? (
                            <>
                              {it.delta_vs_expected > 0 ? '+' : ''}{fmt(it.delta_vs_expected)}
                            </>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}>
                            <Icon className="h-3 w-3" />
                            {meta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 bg-slate-50 border-t text-xs text-slate-600 flex justify-between">
              <span>{filteredSorted.length} di {data?.items?.length || 0} immobili</span>
              <span>Totale pagato COSTS {year}: <strong>{fmt(data?.totals_paid_costs)}</strong> · Budget totale: <strong>{fmt(data?.totals?.budget)}</strong></span>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
